import React, { useContext, ReactElement } from 'react';
import { LogoWithText } from '@sm/webassets';
import { t } from '@sm/intl';
import { Box } from '@wds/box';
import { sanitizeString } from '@sm/utils';
import HeroFormCTA from '~app/components/HeroFormCTA';
import COPY from '~app/pages/EnterpriseThankYou/copy';
import { PageVariants, EnterpriseThankYouContext } from '~app/pages/EnterpriseThankYou';
import useStyles from './useStyles';

type Props = { pageVariant?: symbol };

const Disclaimer = React.memo(({ pageVariant }: Props): ReactElement | null => {
  const { disclaimer } = useStyles({
    isPremium: pageVariant === PageVariants.premiumCustom,
  });
  return pageVariant === PageVariants.standard ? (
    <Box>
      <div
        translate="no"
        className={disclaimer}
        dangerouslySetInnerHTML={{ __html: sanitizeString(t(COPY.SIGN_UP_DISCLAIMER)) }}
        data-testid="footer__Disclamer"
      />
    </Box>
  ) : null;
});

const Footer = React.memo(({ pageVariant }: Props): ReactElement => {
  const { outline, poweredByText, brandingLogo, brandingTextInner, brandingLogoContainer, brandingText, heroFormCta } =
    useStyles({
      isPremium: pageVariant === PageVariants.premiumCustom,
    });
  const heroFormCTAArgs = {
    ctaFormEmail: t(COPY.SIGN_UP_FORM_CTA_EMAIL_ADDRESS),
    ctaFormEmailPlaceholder: t(COPY.SIGN_UP_FORM_CTA_EMAIL_ADDRESS),
    ctaFormPassword: t(COPY.SIGN_UP_FORM_CTA_PASSWORD),
    ctaFormPasswordPlaceholder: t(COPY.SIGN_UP_FORM_CTA_PASSWORD),
    ctaFormSubmit: t(COPY.SIGN_UP_FORM_CTA_SUBMIT),
    disclaimer: t(COPY.SIGN_UP_DISCLAIMER),
    ctaFormPrivacyConsent: 'privacy consent',
    ctaFormNewsletterOptOut: 'newsletter opt out',
    utSource: 'enterprise_thank_you',
    utSource2: 'rebrand_ep_form',
    showThirdPartySignUpActions: false,
    showDisclaimer: false,
    isEnterpriseThankYou: true,
  };
  return (
    <Box data-testid="footer__Footer" className={outline}>
      <Box display="flex" flexJustify="center">
        {/* used <p> instead of Typography to match marketing style */}
        <p className={poweredByText}>{t(COPY.POWERED_BY_TEXT)}</p>
      </Box>
      <Box display="flex" flexJustify="center" className={brandingLogoContainer}>
        <LogoWithText className={brandingLogo} color="alt" />
      </Box>
      <Box className={brandingText} display="flex" flexJustify="center">
        {/* used <p> instead of Typography to match marketing style */}
        <p className={brandingTextInner}>{t(COPY.SIGN_UP_CALLOUT_SUB_HEADER)}</p>
      </Box>
      {pageVariant === PageVariants.standard ? (
        <Box display="flex" flexJustify="center">
          <HeroFormCTA className={heroFormCta} {...heroFormCTAArgs} />
        </Box>
      ) : null}
    </Box>
  );
});

function EnterpriseThankYouFooter(): ReactElement {
  const { pageVariant } = useContext(EnterpriseThankYouContext);
  return (
    <>
      <Footer pageVariant={pageVariant} />
      <Disclaimer pageVariant={pageVariant} />
    </>
  );
}

export default EnterpriseThankYouFooter;
